/**
 * Menu-visibility context.
 * Extracted to its own module (rather than living inside AppShell.jsx) so
 * AccountMenu / header widgets can import it without pulling in the whole
 * AppShell — that would produce a circular import cycle at module init.
 *
 * The AppShell provides the real value at runtime; components rendered
 * outside the shell (auth pages, storybook) get the safe default from
 * `createContext` — all helpers return false so nothing is inadvertently
 * hidden and no "Coming Soon" / "New" chips flash before the layout
 * has loaded.
 */
import { createContext, useContext } from "react";

export const MenuVisibilityContext = createContext({
  disabled: new Set(),
  comingSoon: new Set(),
  isNew: new Set(),
  pageElementsDisabled: new Set(),
  planGated: new Set(),
  // Per-dropdown-parent map of child ids whose `plans` list excludes the
  // current user. Populated by the backend `_plan_allowed_children`
  // filter — see routers/menu_directory.py. Empty for admins /
  // super-admins / contributors (they bypass all plan gates).
  childrenAllowed: {},
  // Raw per-parent per-child overrides map from Menu Directory —
  // `{ [parentId]: { [childId]: { label, order, disabled, coming_soon,
  //   plans } } }`. Pages like Course Workbook read this so the
  // Menu Directory `label` / `order` remain the single source of
  // truth for child names + display ordering everywhere in the app.
  childrenOverrides: {},
  isDisabled: () => false,
  isComingSoon: () => false,
  hasNewBadge: () => false,
  // Returns true when an admin has flipped a specific page-element
  // (e.g. the "Add to LinkedIn" button on /progress) to Visibility Off
  // in Admin Console → Menu Directory → Pages.
  isPageElementHidden: () => false,
  // Returns true when a specific child (e.g. a Financial Tool) is
  // plan-gated OFF for this user under a dropdown parent. Backed by
  // Menu Directory → Children → `plans` — the single source of truth
  // for plan-based feature access. Never introduce a parallel
  // hardcoded plan list in page code.
  isChildPlanGated: () => false,
  // Returns true when a top-level nav id (or section id) is plan-gated
  // for this user. The sidebar renders these rows with an "Upgrade to
  // Access" chip that routes to /account/subscription instead of the
  // gated page.
  isPlanGated: () => false,
});

export function useMenuVisibility() {
  return useContext(MenuVisibilityContext);
}
